// The admin panel's Usage page: which organizations actually use calls and meetings, how much, and when they were last
// active; and one organization's meetings and calls, who attended each, and every person's minutes.
import { Fragment, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { formatShort } from "../../lib/chat";
import { Alert, Badge, Card, Spinner, cx } from "../../components/ui";
import { Header, P, Stat, errorText } from "./index";

const PERIODS = [7, 30, 90];
const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
const duration = (m: number) => (m < 60 ? `${m} min` : `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60} min` : ""}`);
const when = (iso: string | null) => (iso ? formatShort(iso) : "—");
const dayLabel = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString([], { day: "numeric", month: "short" });

type Day = { day: string; calls: number; personMinutes: number };
type OrgRow = {
  id: string; name: string; slug: string; suspended: boolean; accessLimited: boolean; plan: string; members: number;
  calls: number; meetings: number; minutes: number; personMinutes: number; people: number; guests: number;
  recordings: number; transcripts: number; messages: number; lastCallAt: string | null; lastActiveAt: string | null;
};
type Usage = {
  from: string; to: string; days: Day[]; organizations: OrgRow[];
  totals: { organizations: number; organizationsInCalls: number; calls: number; meetings: number; minutes: number; personMinutes: number; people: number; guests: number; recordings: number; transcripts: number };
};
type Session = { id: string; kind: "meeting" | "call"; media: string; title: string; host: string | null; startedAt: string; endedAt: string | null; live: boolean; minutes: number; attendees: number; guests: number; personMinutes: number };
type PersonUsage = { id: string; name: string; email: string; role: string; status: string; meetings: number; meetingMinutes: number; calls: number; callMinutes: number; messages: number; recordings: number };
type OrgUsage = { organization: { id: string; name: string; slug: string }; from: string; to: string; sessions: Session[]; people: PersonUsage[]; days: Day[] };
type Attendance = { people: { name: string; email: string | null; guest: boolean; firstJoined: string; lastLeft: string | null; stillThere: boolean; minutes: number; joins: number }[] };

// The period (7, 30 or 90 days, kept in the address so a link keeps it) as the API's from/to.
function usePeriod() {
  const [params, setParams] = useSearchParams();
  const days = PERIODS.includes(Number(params.get("days"))) ? Number(params.get("days")) : 30;
  const query = useMemo(() => {
    const to = new Date();
    const from = new Date(to.getTime() - days * 86_400_000);
    return new URLSearchParams({ from: from.toISOString(), to: to.toISOString(), tz: TZ }).toString();
  }, [days]);
  const pick = (
    <select value={days} onChange={(e) => setParams({ days: e.target.value }, { replace: true })} aria-label="Period"
      className="rounded-lg border border-slate-300 bg-white px-2 py-2 text-sm dark:border-slate-700 dark:bg-slate-900">
      {PERIODS.map((d) => <option key={d} value={d}>Last {d} days</option>)}
    </select>
  );
  return { days, query, pick };
}

function DayChart({ days, title }: { days: Day[]; title: string }) {
  const [picked, setPicked] = useState<string | null>(null);
  const max = Math.max(1, ...days.map((d) => d.personMinutes));
  const total = days.reduce((n, d) => n + d.personMinutes, 0);
  return (
    <Card>
      <h2 className="font-semibold">{title}</h2>
      <p className="text-sm text-slate-500">Person-minutes in calls and meetings each day ({duration(total)} in all). Tap a bar for its numbers.</p>
      <div className="mt-6 flex h-40 items-end gap-0.5 sm:gap-1" role="img" aria-label={`${title}: ${days.map((d) => `${d.day} ${d.personMinutes} minutes`).join(", ")}`}>
        {days.map((d) => (
          <button type="button" key={d.day} aria-label={`${dayLabel(d.day)}: ${d.personMinutes} person-minutes in ${d.calls} calls`}
            onClick={() => setPicked((p) => (p === d.day ? null : d.day))} onMouseEnter={() => setPicked(d.day)} onMouseLeave={() => setPicked(null)}
            className="relative flex h-full flex-1 flex-col items-center justify-end">
            {picked === d.day && (
              <span className="pointer-events-none absolute -top-12 z-10 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-xs text-white">
                {dayLabel(d.day)}: {duration(d.personMinutes)} · {d.calls} {d.calls === 1 ? "call" : "calls"}
              </span>
            )}
            <div className={cx("w-full rounded-t", d.personMinutes ? "bg-brand-500/80" : "bg-slate-200 dark:bg-slate-700")} style={{ height: d.personMinutes ? `${Math.max(3, (d.personMinutes / max) * 100)}%` : "2px" }} />
          </button>
        ))}
      </div>
      {days.length > 0 && <div className="mt-2 flex justify-between text-xs text-slate-400"><span>{dayLabel(days[0]!.day)}</span><span>{dayLabel(days.at(-1)!.day)}</span></div>}
    </Card>
  );
}

const th = "px-3 py-3 font-semibold";
const num = "px-3 py-2.5 text-right tabular-nums";

export function PlatformUsage() {
  const { days, query, pick } = usePeriod();
  const [all, setAll] = useState(false);
  const data = useQuery({ queryKey: ["platform-usage", query], queryFn: () => api<Usage>(`${P}/usage?${query}`) });
  const u = data.data;
  const rows = (u?.organizations ?? []).filter((o) => all || o.calls + o.meetings > 0);
  return (
    <>
      <Header title="Usage" subtitle="Who uses calls and meetings, how much, and when they were last active."
        action={<div className="flex flex-wrap items-center gap-2">{pick}<a href={`/api/v1${P}/usage?${query}&format=csv`} className="rounded-lg px-3 py-2 text-sm font-medium text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-700/30">Download CSV</a></div>} />
      {data.error && <Alert>{errorText(data.error)}</Alert>}
      {!u ? <div className="flex justify-center p-10 text-brand-600"><Spinner /></div> : (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat label="Organizations in calls" value={`${u.totals.organizationsInCalls} of ${u.totals.organizations}`} detail={`used calls or meetings in ${days} days`} />
            <Stat label="Calls and meetings" value={u.totals.calls + u.totals.meetings} detail={`${u.totals.calls} calls · ${u.totals.meetings} meetings`} />
            <Stat label="Time in calls" value={duration(u.totals.minutes)} detail={`${u.totals.personMinutes} person-minutes`} />
            <Stat label="People in calls" value={u.totals.people} detail={`+ ${u.totals.guests} guests · ${u.totals.recordings} recordings · ${u.totals.transcripts} transcripts`} />
          </div>
          <DayChart days={u.days} title="Minutes in calls per day" />
          <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-semibold">Organizations</h2>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} className="h-4 w-4" />Show organizations with no calls</label>
            </div>
            <Card className="overflow-x-auto p-0">
              <table className="w-full min-w-[1000px] text-left text-sm">
                <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800">
                  <tr><th className={th}>Organization</th><th className={`${th} text-right`}>Calls</th><th className={`${th} text-right`}>Meetings</th><th className={`${th} text-right`}>Time in calls</th><th className={`${th} text-right`}>Person-min</th><th className={`${th} text-right`}>People</th><th className={`${th} text-right`}>Guests</th><th className={`${th} text-right`}>Rec.</th><th className={`${th} text-right`}>Transcripts</th><th className={`${th} text-right`}>Messages</th><th className={th}>Last call</th><th className={th}>Last active</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {rows.map((o) => (
                    <tr key={o.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                      <td className="px-3 py-2.5">
                        <Link to={`/admin/usage/${o.id}?days=${days}`} className="font-medium text-brand-700 hover:underline dark:text-brand-100">{o.name}</Link>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1 text-xs text-slate-500">{o.members} {o.members === 1 ? "member" : "members"}<Badge tone={o.plan === "free" ? "slate" : "brand"}>{o.plan}</Badge>{o.suspended && <Badge tone="red">Suspended</Badge>}{!o.suspended && o.accessLimited && <Badge tone="amber">Limited</Badge>}</div>
                      </td>
                      <td className={num}>{o.calls}</td><td className={num}>{o.meetings}</td><td className={num}>{duration(o.minutes)}</td><td className={num}>{o.personMinutes}</td>
                      <td className={num}>{o.people}</td><td className={num}>{o.guests}</td><td className={num}>{o.recordings}</td><td className={num}>{o.transcripts}</td><td className={num}>{o.messages}</td>
                      <td className="px-3 py-2.5 text-xs text-slate-500">{when(o.lastCallAt)}</td><td className="px-3 py-2.5 text-xs text-slate-500">{when(o.lastActiveAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length === 0 && <p className="p-8 text-center text-sm text-slate-500">No organization used calls or meetings in the last {days} days.</p>}
            </Card>
            <p className="mt-2 text-xs text-slate-500">Time in calls: from the first person joining to the last leaving. Person-minutes: everyone's time added up. Last active: the latest call, message or project change, at any time.</p>
          </div>
        </div>
      )}
    </>
  );
}

function SessionRow({ orgId, s }: { orgId: string; s: Session }) {
  const [open, setOpen] = useState(false);
  const att = useQuery({ queryKey: ["platform-attendance", s.id], queryFn: () => api<Attendance>(`${P}/usage/${orgId}/sessions/${s.id}`), enabled: open });
  return (
    <Fragment>
      <tr className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50" onClick={() => setOpen((o) => !o)}>
        <td className="px-3 py-2.5 text-xs text-slate-500">{when(s.startedAt)}</td>
        <td className="px-3 py-2.5"><Badge tone={s.kind === "meeting" ? "brand" : "slate"}>{s.kind}{s.media === "audio" ? " (audio)" : ""}</Badge>{s.live && <span className="ml-1"><Badge tone="red">live</Badge></span>}</td>
        <td className="px-3 py-2.5"><button type="button" aria-expanded={open} className="text-left font-medium text-brand-700 hover:underline dark:text-brand-100">{s.title}</button></td>
        <td className="px-3 py-2.5 text-slate-600 dark:text-slate-300">{s.host ?? "—"}</td>
        <td className={num}>{duration(s.minutes)}</td><td className={num}>{s.attendees}</td><td className={num}>{s.guests}</td><td className={num}>{s.personMinutes}</td>
      </tr>
      {open && (
        <tr><td colSpan={8} className="bg-slate-50 px-6 py-3 dark:bg-slate-800/40">
          {!att.data ? <Spinner /> : (
            <ul className="space-y-1 text-sm">
              {att.data.people.map((p, i) => (
                <li key={i} className="flex flex-wrap items-center gap-x-3">
                  <span className="font-medium">{p.name}</span>{p.guest ? <Badge tone="amber">guest</Badge> : <span className="text-xs text-slate-500">{p.email}</span>}
                  <span className="text-slate-600 dark:text-slate-300">{duration(p.minutes)}{p.joins > 1 ? ` · joined ${p.joins} times` : ""}</span>
                  <span className="text-xs text-slate-500">{when(p.firstJoined)}{p.stillThere ? " · still there" : ""}</span>
                </li>
              ))}
            </ul>
          )}
        </td></tr>
      )}
    </Fragment>
  );
}

export function PlatformOrgUsage() {
  const { orgId = "" } = useParams();
  const { days, query, pick } = usePeriod();
  const data = useQuery({ queryKey: ["platform-org-usage", orgId, query], queryFn: () => api<OrgUsage>(`${P}/usage/${orgId}?${query}`) });
  const u = data.data;
  if (data.error) return <Alert>{errorText(data.error)}</Alert>;
  if (!u) return <div className="flex justify-center p-10 text-brand-600"><Spinner /></div>;
  const calls = u.sessions.filter((s) => s.kind === "call"), meetings = u.sessions.filter((s) => s.kind === "meeting");
  const people = [...u.people].sort((a, b) => b.callMinutes + b.meetingMinutes - (a.callMinutes + a.meetingMinutes) || b.messages - a.messages);
  const active = people.filter((p) => p.calls + p.meetings > 0).length;
  return (
    <>
      <Link to={`/admin/usage?days=${days}`} className="text-sm text-brand-600 hover:underline">← Usage</Link>
      <Header title={u.organization.name} subtitle={<>/o/{u.organization.slug} · <Link to={`/admin/organizations/${u.organization.id}`} className="text-brand-600 hover:underline">Organization page</Link></>}
        action={<div className="flex flex-wrap items-center gap-2">{pick}<a href={`/api/v1${P}/usage/${u.organization.id}?${query}&format=csv`} className="rounded-lg px-3 py-2 text-sm font-medium text-brand-600 hover:bg-brand-50 dark:hover:bg-brand-700/30">Download CSV</a></div>} />
      <div className="space-y-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Calls" value={calls.length} detail={duration(calls.reduce((n, s) => n + s.minutes, 0))} />
          <Stat label="Meetings" value={meetings.length} detail={duration(meetings.reduce((n, s) => n + s.minutes, 0))} />
          <Stat label="People in calls" value={`${active} of ${people.length}`} detail={`+ ${u.sessions.reduce((n, s) => n + s.guests, 0)} guest visits`} />
          <Stat label="Person-minutes" value={u.sessions.reduce((n, s) => n + s.personMinutes, 0)} detail={`last ${days} days`} />
        </div>
        <DayChart days={u.days} title="Minutes in calls per day" />
        <div>
          <h2 className="mb-1 text-lg font-semibold">Meetings and calls</h2>
          <p className="mb-3 text-sm text-slate-500">Newest first. Click one to see who attended and for how long.</p>
          <Card className="overflow-x-auto p-0">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800">
                <tr><th className={th}>Started</th><th className={th}>Type</th><th className={th}>Title</th><th className={th}>Host</th><th className={`${th} text-right`}>Duration</th><th className={`${th} text-right`}>People</th><th className={`${th} text-right`}>Guests</th><th className={`${th} text-right`}>Person-min</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">{u.sessions.map((s) => <SessionRow key={s.id} orgId={u.organization.id} s={s} />)}</tbody>
            </table>
            {u.sessions.length === 0 && <p className="p-8 text-center text-sm text-slate-500">No calls or meetings in the last {days} days.</p>}
          </Card>
        </div>
        <div>
          <h2 className="mb-3 text-lg font-semibold">People</h2>
          <Card className="overflow-x-auto p-0">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800">
                <tr><th className={th}>Person</th><th className={th}>Role</th><th className={`${th} text-right`}>Calls</th><th className={`${th} text-right`}>Call time</th><th className={`${th} text-right`}>Meetings</th><th className={`${th} text-right`}>Meeting time</th><th className={`${th} text-right`}>Messages</th><th className={`${th} text-right`}>Recordings</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {people.map((p) => (
                  <tr key={p.id} className={cx(p.calls + p.meetings + p.messages === 0 && "text-slate-400")}>
                    <td className="px-3 py-2.5"><span className="font-medium">{p.name}</span><div className="text-xs text-slate-500">{p.email}</div></td>
                    <td className="px-3 py-2.5"><Badge>{p.role}</Badge></td>
                    <td className={num}>{p.calls}</td><td className={num}>{duration(p.callMinutes)}</td><td className={num}>{p.meetings}</td><td className={num}>{duration(p.meetingMinutes)}</td><td className={num}>{p.messages}</td><td className={num}>{p.recordings}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      </div>
    </>
  );
}
